// ============================================================================
import { logger } from '../logger.js';
// Host Bridge — Main Thread API (Tier 1 ↔ Tier 2)
// ============================================================================
// RC-03: Types and interfaces extracted to host-bridge-types.ts.
// This file re-exports them for backward compatibility.
import { allocateControlMemory, allocateFrameBuffer, addRef, releaseRef, createBufferIdGenerator } from '../memory/allocator.js';
import { ParamWriter, readError, clearError } from '../memory/param-buffer.js';
import { RingProducer } from '../memory/ring-buffer.js';
import { DescriptorTable } from '../memory/descriptor-table.js';
import { compileSchema } from '../schema/compiler.js';
import { BufferFormat, BufferFlags, DEFAULT_CONFIG, } from '../types.js';
import { FeatureRegistry } from '../features/registry.js';
import { KernelRegistry } from '../compute/kernel-registry.js';
import { TickLoop } from './tick-loop.js';
import { ParamReader } from '../memory/param-buffer.js';
import { RingConsumer } from '../memory/ring-buffer.js';
import { DefaultCompositor } from '../render/default-compositor.js';
// Re-export types from the extracted types module
export { READBACK, READBACK_SAB_SIZE, READBACK_FIELDS_DEF, RB_DIRTY, RB_HAS_SELECTION, RB_SEL_X, RB_SEL_Y, RB_SEL_W, RB_SEL_H, RB_HAS_TRANSFORM, RB_TFM_DATA, RB_STROKE_ON, RB_LAYER_COUNT, RB_ACTIVE_LAYER, } from './host-bridge-types.js';
import { READBACK_SAB_SIZE, RB_DIRTY } from './host-bridge-types.js';
/**
 * HostBridge — the primary public API for @alloyhead/core.
 *
 * The HostBridge manages the communication layer between the main thread (host)
 * and the compute engine, which can run either in a Web Worker (off main thread)
 * or in single-thread fallback mode.
 *
 * **Lifecycle:**
 * 1. Construct: `new HostBridge(schema, config, callbacks)`
 * 2. Initialize: `await bridge.init(canvas, engineTick)` (single-thread)
 *    or `await bridge.initWorker(canvas)` (worker mode)
 * 3. Run: `bridge.start()` — begins the render loop
 * 4. Interact: `bridge.setParam()`, `bridge.pushCommand()`, `bridge.pushSideband()`
 * 5. Destroy: `bridge.destroy()` — terminates worker, frees all resources
 *
 * **Shared Memory Architecture:**
 * - Param buffer: lock-free read/write of named parameters (cursor, brush size, etc.)
 * - Ring buffer: SPSC command queue (host produces, engine consumes)
 * - Descriptor table: composite layer ordering + dirty tracking
 * - Readback SAB: engine→host state (dirty flag, selection bounds, transform)
 *
 * **Modes:**
 * - Worker mode: engine runs in a Web Worker, canvas is transferred via OffscreenCanvas
 * - Single-thread mode: engine runs on the main thread with requestAnimationFrame
 * - Both modes share the same HostBridge API — the mode is transparent to callers
 *
 * @example
 * ```ts
 * import { HostBridge } from '@alloyhead/core';
 *
 * const bridge = new HostBridge(SCHEMA, {}, {
 *   onFrame: (stats) => console.log(`${stats.fps} fps`),
 *   onError: (code, msg) => console.error(msg),
 * });
 *
 * await bridge.initWorker(document.getElementById('canvas'));
 * bridge.setParam('brushSize', 12);
 * bridge.pushSimple(CMD.UNDO);
 * bridge.start();
 * ```
 */
export class HostBridge {
    schema;
    config;
    layout;
    paramWriter;
    ringProducer;
    descriptorTable;
    callbacks;
    buffers = new Map();
    nextBufferId;
    destroyed = false;
    /** Runtime feature gating. Check `bridge.features.isEnabled('x')` before gated operations. */
    features;
    /** Named compute kernel registry. Register kernels at init, run by name. */
    kernels;
    // Worker mode
    worker = null;
    // Pending buffer destroy acknowledgments from worker
    pendingDestroyAcks = new Map();
    // Fallback: single-thread mode
    singleThread = false;
    engineTick = null;
    wasmAdapter = null;
    canvas = null;
    ctx2d = null;
    // Shared tick loop — owns FPS tracking, circuit breaker, stats reporting.
    // Created lazily in single-thread init paths.
    tickLoop = null;
    // Fallback reader/consumer for single-thread mode
    paramReader;
    ringConsumer;
    // --- Readback SAB (overlay state) ---
    // Dedicated SharedArrayBuffer for high-frequency engine state that the overlay
    // canvas needs. Written by the engine after each tick, read by the main thread.
    readbackSAB = null;
    readbackView = null;
    // --- Sideband MessageChannel (worker mode) ---
    // Complex-payload commands that don't fit in the ring buffer are sent via
    // a dedicated MessageChannel. In single-thread mode, sideband calls go
    // directly to the adapter.
    sidebandChannel = null;
    // --- Worker→Host transfer caches (worker mode) ---
    // Selection mask — transferred from worker via postMessage(Transferable)
    selectionMaskCache = null;
    // Transform preview — transferred from worker via ImageBitmap
    transformDataCache = null;
    // Export — resolved when worker sends export-result
    exportResolve = null;
    // Dims changed callback
    onDimsChanged = null;
    // Adapter ready — resolved when the worker's adapter finishes async init
    adapterReadyResolve = null;
    adapterReadyPromise = null;
    // --- Render (single-thread mode) ---
    // DefaultCompositor handles checkerboard + dirty-aware layer compositing.
    compositor = new DefaultCompositor();
    /**
     * Create a new HostBridge instance.
     *
     * @param schemaJson - Schema definition (params, commands, buffers). Compiled internally.
     * @param config - Framework configuration overrides. Defaults are tuned for typical editors.
     *   - `paramBufferSize`: Shared param buffer size in bytes (default: 4096)
     *   - `commandSlotSize`: Bytes per ring buffer command slot (default: 64)
     *   - `ringBufferSlots`: Number of ring buffer slots (default: 256)
     *   - `descriptorSlots`: Max number of descriptor table entries (default: 64)
     *   - `featureTier`: Feature gating tier (`'free'` | `'pro'` | `'enterprise'`)
     * @param callbacks - Optional event handlers for frame stats, errors, readiness, etc.
     */
    constructor(schemaJson, config = {}, callbacks = {}) {
        this.schema = compileSchema(schemaJson);
        const { featureTier, ...frameworkConfig } = config;
        this.config = {
            ...DEFAULT_CONFIG,
            ...frameworkConfig,
            paramBufferSize: Math.max(DEFAULT_CONFIG.paramBufferSize, this.schema.paramBufferSize),
        };
        this.callbacks = callbacks;
        this.nextBufferId = createBufferIdGenerator();
        this.features = new FeatureRegistry(featureTier);
        this.kernels = new KernelRegistry();
        this.kernels.setFeatureRegistry(this.features);
    }
    // --- Initialization ---
    /**
     * Initialize in single-thread mode with a JS engine tick function.
     *
     * The engine runs on the main thread via requestAnimationFrame.
     * This is the default mode and works everywhere, including browsers
     * without SharedArrayBuffer support.
     *
     * @param canvas - The canvas element to render to
     * @param engineTick - The compute engine's tick function (runs on main thread)
     */
    async init(canvas, engineTick) {
        this.allocateSharedMemory();
        this.initSingleThreadMode(canvas, engineTick);
    }
    /**
     * Initialize in Worker mode with an off-thread orchestrator.
     *
     * The engine runs inside the Web Worker. The workerUrl must point to a
     * script that imports the engine and calls setEngineTick(). Functions
     * cannot be transferred across thread boundaries — the worker script
     * must bundle or import its own engine code.
     *
     * Requires: SharedArrayBuffer (COOP/COEP headers) + OffscreenCanvas.
     *
     * @param canvas - The canvas element (control is transferred to the worker)
     * @param workerUrl - URL to the worker script (must be an ES module)
     * @param wasmUrl - Optional: URL to a .wasm binary the worker should load
     */
    async initWorker(canvas, workerUrl, wasmUrl) {
        this.verifyWorkerPrereqs();
        this.allocateSharedMemory();
        await this.initWorkerMode(canvas, workerUrl, wasmUrl?.toString());
    }
    /**
     * Initialize in Worker mode with an ES module engine.
     *
     * The worker dynamically imports the module at moduleUrl, which must
     * default-export an EngineTick function or call setEngineTick().
     *
     * @param canvas - The canvas element (control is transferred to the worker)
     * @param workerUrl - URL to the orchestrator worker script
     * @param moduleUrl - URL to the engine ES module (dynamically imported in worker)
     */
    async initWorkerWithModule(canvas, workerUrl, moduleUrl) {
        this.verifyWorkerPrereqs();
        this.allocateSharedMemory();
        await this.initWorkerMode(canvas, workerUrl, undefined, moduleUrl.toString());
    }
    verifyWorkerPrereqs() {
        if (typeof SharedArrayBuffer === 'undefined') {
            throw new Error('SharedArrayBuffer is not available. Worker mode requires COOP/COEP headers: ' +
                'Cross-Origin-Opener-Policy: same-origin and Cross-Origin-Embedder-Policy: require-corp');
        }
        if (typeof OffscreenCanvas === 'undefined') {
            throw new Error('OffscreenCanvas is not available. Worker mode requires a browser that supports OffscreenCanvas.');
        }
    }
    /**
     * Initialize with a Wasm engine adapter.
     *
     * **Zero-config worker mode:** If `options.adapterUrl` is provided and the browser
     * supports SharedArrayBuffer + OffscreenCanvas, the framework automatically spawns
     * a worker, imports the adapter factory module, creates the adapter inside the worker,
     * and wires all hooks. No custom worker script needed.
     *
     * **Single-thread fallback:** If prerequisites aren't met or no adapterUrl is provided,
     * the adapter runs on the main thread with the same framework-managed tick loop.
     *
     * @param canvas - The canvas element to render to
     * @param adapter - The Wasm engine adapter (used in single-thread mode)
     * @param options - Optional: adapterUrl for auto-detect worker mode, forceMode override
     */
    async initWithWasm(canvas, adapter, options) {
        const adapterUrl = options?.adapterUrl?.toString();
        const forceMode = options?.forceMode;
        // Auto-detect: use worker mode if we have adapterUrl + browser prerequisites
        const canWorker = forceMode !== 'single' &&
            !!adapterUrl &&
            typeof SharedArrayBuffer !== 'undefined' &&
            typeof OffscreenCanvas !== 'undefined';
        if (canWorker || forceMode === 'worker') {
            if (!adapterUrl) {
                throw new Error('Worker mode requires options.adapterUrl — a URL to a module that default-exports an async adapter factory: (w, h) => Promise<WasmEngineAdapter>');
            }
            // ══════════ AUTO-DETECT WORKER MODE ══════════
            // The framework generates the worker internally using the orchestrator.
            // The adapterUrl module is dynamically imported inside the worker.
            this.verifyWorkerPrereqs();
            this.allocateSharedMemory();
            await this.initWorkerMode(canvas, this.getOrchestratorWorkerUrl(), undefined, undefined, adapterUrl);
            return;
        }
        // ══════════ SINGLE-THREAD FALLBACK ══════════
        this.allocateSharedMemory();
        this.singleThread = true;
        this.canvas = canvas;
        this.ctx2d = canvas.getContext('2d');
        this.wasmAdapter = adapter;
        // Create reader/consumer for engine side
        this.paramReader = new ParamReader(this.layout.controlSAB, this.layout.paramOffset, this.schema.paramLayout);
        this.ringConsumer = new RingConsumer(this.layout.controlSAB, this.layout.ringOffset, this.schema.slotSize);
        // Wrap the adapter into an EngineTick for the unified tick loop
        // Pre-bind the readParam closure outside the hot path to avoid per-frame allocation.
        const paramReaderFn = (name) => this.paramReader.read(name);
        const readbackView = this.readbackView;
        this.engineTick = (ctx) => {
            // 1. Drain commands → adapter
            ctx.drainCommands((cmd) => {
                adapter.pushCommand(cmd.type, cmd.payloadView);
            });
            // 2. Push params → adapter (uses pre-bound reader — no closure alloc)
            adapter.pushParams(paramReaderFn);
            // 3. Tick — use real wall-clock dt from the tick context
            const status = adapter.tick(ctx.dt);
            // 4. Write readback state for overlay (selection, transform, dirty, etc.)
            if (adapter.writeReadback) {
                adapter.writeReadback(readbackView);
            }
            // 5. Layer sync — post snapshot to main thread callback when dirty
            if (adapter.getLayerSnapshot && this.callbacks.onLayerSync) {
                // Check the dirty flag in readback to decide if layer sync is needed
                const dirty = readbackView.getUint32(RB_DIRTY, true);
                if (dirty) {
                    this.callbacks.onLayerSync(adapter.getLayerSnapshot());
                }
            }
            // 6. Custom render (if provided) — otherwise default compositing handles it
            if (adapter.render && this.ctx2d && this.canvas) {
                adapter.render(this.ctx2d, this.canvas.width, this.canvas.height);
            }
            return status;
        };
        this.createTickLoop();
        this.callbacks.onReady?.();
    }
    allocateSharedMemory() {
        // Allocate control buffer
        this.layout = allocateControlMemory(this.config);
        // Create main-thread param writer + ring producer
        this.paramWriter = new ParamWriter(this.layout.controlSAB, this.layout.paramOffset, this.schema.paramLayout);
        this.ringProducer = new RingProducer(this.layout.controlSAB, this.layout.ringOffset, this.schema.slotSize);
        this.descriptorTable = new DescriptorTable(this.layout.controlSAB, this.layout.descriptorOffset, this.layout.maxDescriptorEntries);
        // Allocate readback SAB for overlay state (selection, transform, dirty, etc.)
        this.readbackSAB = typeof SharedArrayBuffer !== 'undefined'
            ? new SharedArrayBuffer(READBACK_SAB_SIZE)
            : new ArrayBuffer(READBACK_SAB_SIZE);
        this.readbackView = new DataView(this.readbackSAB);
    }
    async initWorkerMode(canvas, workerUrl, wasmUrl, moduleUrl, adapterModuleUrl) {
        const offscreen = canvas.transferControlToOffscreen();
        this.canvas = canvas;
        this.worker = new Worker(workerUrl, { type: 'module' });
        // Create sideband MessageChannel for complex-payload commands
        this.sidebandChannel = new MessageChannel();
        // Create the adapter-ready promise before the worker starts so it is never
        // missed even if adapter-ready arrives before the caller awaits it.
        this.adapterReadyPromise = new Promise((resolve) => {
            this.adapterReadyResolve = resolve;
        });
        return new Promise((resolve, reject) => {
            const timeoutMs = this.config.workerInitTimeoutMs;
            const timeout = setTimeout(() => reject(new Error(`Worker init timeout (${timeoutMs}ms). Increase config.workerInitTimeoutMs for slow networks.`)), timeoutMs);
            this.worker.onmessage = (e) => {
                const msg = e.data;
                switch (msg.type) {
                    case 'ready':
                        clearTimeout(timeout);
                        this.callbacks.onReady?.();
                        resolve();
                        break;
                    case 'frame':
                        this.callbacks.onFrame?.(msg);
                        break;
                    case 'error':
                        this.callbacks.onError?.(msg.code, msg.message);
                        break;
                    case 'buffer-destroyed': {
                        // Worker has released its reference — safe for host to release too
                        const ack = this.pendingDestroyAcks.get(msg.bufferId);
                        if (ack) {
                            ack();
                            this.pendingDestroyAcks.delete(msg.bufferId);
                        }
                        break;
                    }
                    case 'wasm-loaded':
                        this.callbacks.onWasmLoaded?.();
                        break;
                    case 'module-loaded':
                        this.callbacks.onModuleLoaded?.();
                        break;
                    case 'adapter-ready':
                        // Worker's async adapter init is complete — safe to start() now.
                        if (this.adapterReadyResolve) {
                            this.adapterReadyResolve();
                            this.adapterReadyResolve = null;
                            this.adapterReadyPromise = null; // waitForAdapter() returns resolved promise hereafter
                        }
                        break;
                    case 'selection-mask':
                        // Cache selection mask transferred from worker
                        this.selectionMaskCache = {
                            mask: msg.mask,
                            width: msg.width,
                            height: msg.height,
                            x: msg.x, y: msg.y, w: msg.w, h: msg.h,
                        };
                        break;
                    case 'transform-data':
                        // Cache transform preview ImageBitmap from worker
                        // Close previous bitmap to avoid memory leaks
                        if (this.transformDataCache?.bitmap) {
                            this.transformDataCache.bitmap.close();
                        }
                        this.transformDataCache = {
                            bitmap: msg.bitmap,
                            x: msg.x, y: msg.y, w: msg.w, h: msg.h,
                            rot: msg.rot, sx: msg.sx, sy: msg.sy, selOnly: msg.selOnly,
                        };
                        break;
                    case 'layer-sync':
                        this.callbacks.onLayerSync?.(msg.layers);
                        break;
                    case 'export-result':
                        if (this.exportResolve) {
                            this.exportResolve(msg.bitmap);
                            this.exportResolve = null;
                        }
                        break;
                    case 'dims-changed':
                        if (this.onDimsChanged) {
                            this.onDimsChanged(msg.width, msg.height);
                        }
                        break;
                }
            };
            this.worker.onerror = (e) => {
                clearTimeout(timeout);
                reject(new Error(`Worker failed to load: ${e.message}`));
            };
            // Send init message with SABs and OffscreenCanvas.
            // Note: SABs are shared by reference (not transferred).
            // OffscreenCanvas IS transferred (main thread loses access).
            // Sideband port2 IS transferred to the worker.
            const frameSABEntries = [];
            for (const [id, handle] of this.buffers) {
                frameSABEntries.push([id, handle.sab]);
            }
            const sidebandPort = this.sidebandChannel.port2;
            this.worker.postMessage({
                type: 'init',
                config: this.config,
                canvas: offscreen,
                controlSAB: this.layout.controlSAB,
                readbackSAB: this.readbackSAB,
                paramLayout: this.schema.paramLayout,
                paramOffset: this.layout.paramOffset,
                ringOffset: this.layout.ringOffset,
                descriptorOffset: this.layout.descriptorOffset,
                frameSABs: frameSABEntries,
                wasmUrl,
                moduleUrl,
                adapterModuleUrl,
                sidebandPort,
            }, [offscreen, sidebandPort]); // Transfer OffscreenCanvas + sideband port
        });
    }
    /**
     * Generate a Blob URL for the framework's generic worker script.
     * This worker imports the orchestrator (which handles message protocol + tick loop).
     * The orchestrator's loadAdapterModule() handles adapter wiring automatically.
     */
    orchestratorBlobUrl = null;
    getOrchestratorWorkerUrl() {
        if (this.orchestratorBlobUrl)
            return this.orchestratorBlobUrl;
        // The worker script simply imports the orchestrator module as a side-effect.
        // The orchestrator installs onmessage + tick loop, and handles adapterModuleUrl.
        // We use the resolved URL of the orchestrator module for the import.
        const orchUrl = new URL('./orchestrator-worker.js', import.meta.url).href;
        const workerCode = `import '${orchUrl}';\n`;
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        this.orchestratorBlobUrl = URL.createObjectURL(blob);
        return this.orchestratorBlobUrl;
    }
    initSingleThreadMode(canvas, engineTick) {
        this.singleThread = true;
        this.canvas = canvas;
        this.ctx2d = canvas.getContext('2d');
        this.engineTick = engineTick;
        // Create reader/consumer for engine side
        this.paramReader = new ParamReader(this.layout.controlSAB, this.layout.paramOffset, this.schema.paramLayout);
        this.ringConsumer = new RingConsumer(this.layout.controlSAB, this.layout.ringOffset, this.schema.slotSize);
        this.createTickLoop();
        this.callbacks.onReady?.();
    }
    /**
     * Creates the shared TickLoop for single-thread mode. Both `init()` and
     * `initWithWasm()` converge here — the only difference is what `engineTick`
     * was set to before this is called.
     */
    createTickLoop() {
        // Pre-allocate a mutable TickContext — reused every frame to avoid GC pressure.
        const reusableCtx = {
            dt: 0,
            readParam: (name) => this.paramReader.read(name),
            readAllParams: () => this.paramReader.readAll(),
            drainCommands: (handler) => this.ringConsumer.drain(handler),
            getBufferData: (id) => this.getBufferData(id),
            getCompositeLayers: () => this.descriptorTable.getCompositeLayers(),
            markDirty: (id) => {
                const idx = this.descriptorTable.findById(id);
                if (idx >= 0)
                    this.descriptorTable.markDirty(idx);
            },
            canvasWidth: this.canvas.width,
            canvasHeight: this.canvas.height,
        };
        this.tickLoop = new TickLoop({
            buildContext: (dt) => {
                reusableCtx.dt = dt / 1000;
                reusableCtx.canvasWidth = this.canvas.width;
                reusableCtx.canvasHeight = this.canvas.height;
                return reusableCtx;
            },
            render: () => this.renderSingleThread(),
            onStats: (stats) => {
                this.lastPerf = {
                    tickDurationUs: stats.tickMs * 1000,
                    pixelsProcessed: this.canvas ? this.canvas.width * this.canvas.height : 0,
                    buffersMutated: this.buffers.size,
                    commandsDrained: stats.commandsDrained,
                    scratchBytesUsed: 0,
                    asyncProgress: 0,
                };
                this.callbacks.onFrame?.(stats);
            },
            onError: (code, message) => this.callbacks.onError?.(code, message),
            // Check the error region in the control SAB after each tick.
            // The engine (or Wasm kernel) writes error codes here; we read + clear.
            checkErrorRegion: () => {
                if (!this.layout?.controlSAB)
                    return null;
                const err = readError(this.layout.controlSAB, 0);
                if (err)
                    clearError(this.layout.controlSAB, 0);
                return err;
            },
        }, {
            statsReportIntervalMs: this.config.statsReportIntervalMs,
        });
        this.tickLoop.setEngine(this.engineTick);
    }
    // --- Perf Telemetry ---
    lastPerf = {
        tickDurationUs: 0, pixelsProcessed: 0, buffersMutated: 0,
        commandsDrained: 0, scratchBytesUsed: 0, asyncProgress: 0,
    };
    /**
     * Get the most recent performance telemetry snapshot.
     * Updated every stats report interval (default: 100ms).
     */
    /**
     * Get the latest performance telemetry snapshot.
     * Updated every stats report interval (~100ms by default).
     * Contains FPS, tick time, command throughput, and buffer counts.
     */
    getPerf() {
        return this.lastPerf;
    }
    // --- Buffer Management ---
    /**
     * Allocate a new frame buffer backed by a SharedArrayBuffer.
     *
     * The buffer is registered in the descriptor table and (in worker mode)
     * sent to the worker. Returns a handle with the buffer ID, raw SAB,
     * typed data view, and dimensions.
     *
     * @param width - Buffer width in pixels
     * @param height - Buffer height in pixels
     * @param format - Pixel format (default: RGBA8)
     * @param flags - Buffer flags (default: COMPOSITE | DIRTY)
     * @param layerOrder - Compositing order (0 = bottom)
     * @returns BufferHandle with bufferId, sab, data, width, height
     */
    createBuffer(width, height, format = BufferFormat.RGBA8, flags = BufferFlags.COMPOSITE | BufferFlags.DIRTY, layerOrder = 0) {
        if (this.destroyed)
            throw new Error('HostBridge has been destroyed.');
        const handle = allocateFrameBuffer(this.nextBufferId(), width, height, format);
        this.buffers.set(handle.bufferId, handle);
        // Register in descriptor table
        this.descriptorTable.addEntry({
            bufferId: handle.bufferId,
            bufferPtr: 0,
            width, height,
            stride: handle.stride,
            format,
            flags,
            layerOrder,
        });
        // Tell worker about new buffer
        if (this.worker) {
            addRef(handle); // Worker now holds a reference (host + worker = refCount 2)
            this.worker.postMessage({
                type: 'create-buffer',
                bufferId: handle.bufferId,
                sab: handle.sab,
                width, height,
                stride: handle.stride,
                format, flags, layerOrder,
            });
        }
        return {
            bufferId: handle.bufferId,
            sab: handle.sab,
            data: handle.data,
            width, height,
        };
    }
    /**
     * Destroy a frame buffer.
     *
     * TWO-PHASE RELEASE PROTOCOL (worker mode):
     * The buffer's refCount starts at 2 (host + worker). Destruction proceeds in two phases:
     *
     *   Phase 1 (immediate): Host releases its own reference (refCount 2 → 1) and removes
     *   the buffer from its local maps. The SAB is still alive because the worker holds a ref.
     *   Host also sends 'destroy-buffer' to the worker.
     *
     *   Phase 2 (async): Worker receives 'destroy-buffer', cleans up its local state, and
     *   sends 'buffer-destroyed' ack. The ack callback releases the worker's reference
     *   (refCount 1 → 0). The SAB is now GC-eligible.
     *
     * This two-phase design means the host can immediately stop using the buffer (it's
     * removed from `this.buffers`) without waiting for the async worker roundtrip, while
     * the worker continues to safely access the SAB until it processes the destroy message.
     *
     * In single-thread mode there's no worker, so only the host reference exists and
     * `releaseRef` drops refCount from 1 → 0 immediately.
     */
    destroyBuffer(bufferId) {
        if (this.destroyed)
            return;
        const handle = this.buffers.get(bufferId);
        if (!handle)
            return;
        this.descriptorTable.removeById(bufferId);
        if (this.worker) {
            // Phase 2 callback: when the worker acks, release its reference (refCount 1 → 0).
            this.pendingDestroyAcks.set(bufferId, () => {
                releaseRef(handle);
            });
            this.worker.postMessage({ type: 'destroy-buffer', bufferId });
        }
        // Phase 1: release host reference immediately (refCount 2 → 1 in worker mode, 1 → 0 in single-thread).
        releaseRef(handle);
        this.buffers.delete(bufferId);
    }
    /**
     * Get the raw pixel data for a buffer as a Uint8Array view over the SAB.
     * Returns null if the buffer ID is not found.
     *
     * @param bufferId - The buffer to read
     */
    getBufferData(bufferId) {
        const handle = this.buffers.get(bufferId);
        return handle ? new Uint8Array(handle.sab) : null;
    }
    // --- Parameter Access (Main Thread) ---
    /**
     * Write a parameter value to the shared Parameter Buffer via Atomics.
     * This is the primary way the UI thread communicates continuous state
     * (cursor position, brush size, slider values) to the engine.
     *
     * Non-blocking. Safe to call at 60Hz+ (e.g., on every mousemove).
     *
     * @param name - Parameter name as defined in the schema
     * @param value - Numeric value to write
     */
    setParam(name, value) {
        if (this.destroyed || !this.paramWriter)
            return;
        this.paramWriter.write(name, value);
    }
    /**
     * Read a parameter's current value from the shared Parameter Buffer.
     * Returns 0 if the bridge is destroyed or the param is unknown.
     *
     * @param name - Parameter name as defined in the schema
     */
    getParam(name) {
        if (this.destroyed || !this.paramWriter)
            return 0;
        return this.paramWriter.read(name);
    }
    /**
     * Get the compiled parameter layout (field names, types, byte offsets).
     * Useful for debugging or building generic param inspectors.
     */
    getParamLayout() {
        return this.schema.paramLayout;
    }
    // --- Commands (Main Thread) ---
    /**
     * Push a command to the ring buffer.
     * Returns true if enqueued, false if the buffer is full (command NOT sent).
     * A false return means the engine is not draining fast enough. Log a warning.
     */
    pushCommand(cmdType, flags = 0, writePayload) {
        if (this.destroyed)
            return false;
        const ok = this.ringProducer.push(cmdType, flags, writePayload);
        if (!ok) {
            const pending = this.ringProducer.pending;
            logger.warn(`[Alloyhead] Command ${cmdType} dropped: ring buffer full (${pending} pending). Engine may be stalled.`);
            this.callbacks.onCommandDropped?.(cmdType, pending);
        }
        return ok;
    }
    /**
     * Push a command with no payload (zero-overhead ring buffer write).
     * Use for discrete actions like UNDO, REDO, LAYER_ADD, STROKE_ON.
     *
     * @param cmdType - Command type ID from the schema
     * @returns true if enqueued, false if ring buffer is full
     */
    pushSimple(cmdType) {
        return this.pushCommand(cmdType);
    }
    /**
     * Push a command with a single Uint32 payload value.
     * Convenience wrapper for commands like FILTER(id) or HIST_JUMP(idx).
     *
     * @param cmdType - Command type ID
     * @param value - Uint32 payload value
     * @returns true if enqueued, false if ring buffer is full
     */
    pushU32(cmdType, value) {
        return this.pushCommand(cmdType, 0, (v, off) => v.setUint32(off, value, true));
    }
    /**
     * Push a command with two Float32 payload values.
     * Convenience wrapper for commands like MOVE(dx, dy).
     *
     * @param cmdType - Command type ID
     * @param a - First Float32 value
     * @param b - Second Float32 value
     * @returns true if enqueued, false if ring buffer is full
     */
    pushF32Pair(cmdType, a, b) {
        return this.pushCommand(cmdType, 0, (v, off) => {
            v.setFloat32(off, a, true);
            v.setFloat32(off + 4, b, true);
        });
    }
    /**
     * Number of commands currently in the ring buffer waiting to be drained.
     * Useful for back-pressure monitoring (if consistently > 0, engine is stalled).
     */
    get ringPending() {
        return this.ringProducer?.pending ?? 0;
    }
    // --- Sideband Commands (Main Thread → Worker or Direct) ---
    /**
     * Send a complex-payload command via the sideband MessageChannel.
     * In worker mode, the command is structured-cloned to the worker.
     * In single-thread mode, the command is forwarded directly to the adapter.
     */
    pushSideband(cmdType, payload) {
        if (this.destroyed)
            return;
        if (this.singleThread) {
            // Direct call to adapter in single-thread mode
            this.wasmAdapter?.pushSidebandCommand?.(cmdType, payload);
        }
        else if (this.sidebandChannel) {
            // Send via MessageChannel port1 → worker's port2
            this.sidebandChannel.port1.postMessage({ type: cmdType, payload });
        }
    }
    /**
     * Get the sideband MessageChannel port1 for direct use.
     * In worker mode, allows the app to send complex commands to the worker.
     * Returns null in single-thread mode (use pushSideband instead).
     */
    getSidebandPort() {
        return this.sidebandChannel?.port1 ?? null;
    }
    // --- Worker→Host Transfer Caches ---
    /**
     * Get the cached selection mask (worker mode).
     * Updated by postMessage from the worker when selection changes.
     * Returns null if no selection or in single-thread mode (use engine.s.sel directly).
     */
    getSelectionMask() {
        return this.selectionMaskCache;
    }
    /**
     * Clear the cached selection mask (e.g., when selection is cleared).
     */
    clearSelectionMask() {
        this.selectionMaskCache = null;
    }
    /**
     * Get the cached transform data (worker mode).
     * Updated by postMessage from the worker when transform begins.
     * Returns null if no transform or in single-thread mode.
     */
    getTransformData() {
        return this.transformDataCache;
    }
    /**
     * Clear the cached transform data.
     */
    clearTransformData() {
        if (this.transformDataCache?.bitmap) {
            this.transformDataCache.bitmap.close();
        }
        this.transformDataCache = null;
    }
    /**
     * Request a composited export image from the worker.
     * Returns a Promise that resolves with an ImageBitmap of the full composite.
     * In single-thread mode, calls the adapter's composite() directly.
     */
    async requestExport() {
        if (this.singleThread && this.wasmAdapter?.composite) {
            const canvas = this.wasmAdapter.composite();
            return canvas.transferToImageBitmap();
        }
        if (this.worker) {
            return new Promise((resolve) => {
                this.exportResolve = resolve;
                this.worker.postMessage({ type: 'request-export' });
            });
        }
        throw new Error('No engine available for export');
    }
    /**
     * Register a callback for when the engine's document dimensions change
     * (e.g., after crop, rotate, image resize).
     * In worker mode, this fires from the worker's postMessage.
     */
    setDimsChangedCallback(callback) {
        this.onDimsChanged = callback;
    }
    /**
     * Whether the bridge is running in worker mode (off main thread).
     */
    get isWorkerMode() {
        return !this.singleThread && this.worker !== null;
    }
    /**
     * In worker mode, resolves when the worker's adapter has finished its async
     * initialization (Wasm load, engine create, hook wiring) and is ready to
     * process commands. In single-thread mode resolves immediately.
     *
     * Always await this before calling bridge.start() in worker mode — the tick
     * loop begins draining the ring buffer immediately on start(), and any
     * commands pushed before the adapter is wired are silently dropped.
     */
    waitForAdapter() {
        if (this.singleThread || !this.worker)
            return Promise.resolve();
        if (!this.adapterReadyPromise) {
            // Already resolved (adapter-ready arrived before caller awaited)
            return Promise.resolve();
        }
        return this.adapterReadyPromise;
    }
    // --- Canvas Resize ---
    /**
     * Notify the bridge that the canvas dimensions have changed.
     * In worker mode, sends a resize message to the worker.
     * In single-thread mode, invalidates the render cache.
     */
    /**
     * Update canvas dimensions and re-acquire the 2D context.
     *
     * @param skipAdapter - When true, skip notifying the Wasm adapter. Use this when
     *   the resize was initiated BY the engine (via onDimsChanged) to prevent the
     *   adapter from pushing IMG_RESIZE back into the engine and creating a loop.
     */
    resize(width, height, skipAdapter = false) {
        if (this.destroyed)
            return;
        if (this.canvas) {
            this.canvas.width = width;
            this.canvas.height = height;
            // Re-acquire the 2D context — setting canvas.width/height resets the canvas
            // and invalidates any previously obtained CanvasRenderingContext2D. Subsequent
            // adapter.render() calls would silently draw into a dead context without this.
            if (this.singleThread && !this.worker) {
                this.ctx2d = this.canvas.getContext('2d');
            }
        }
        // Notify Wasm adapter — skip when the resize came FROM the engine to avoid recursion.
        if (!skipAdapter && this.wasmAdapter?.resize) {
            this.wasmAdapter.resize(width, height);
        }
        if (this.worker) {
            this.worker.postMessage({ type: 'resize', width, height });
        }
    }
    // --- Render Loop ---
    /**
     * Start the render/tick loop.
     *
     * In single-thread mode, starts a requestAnimationFrame loop that calls
     * the engine tick + render on each frame. In worker mode, sends a 'start'
     * message to the worker's tick loop.
     */
    start() {
        if (this.destroyed)
            return;
        if (this.singleThread && this.tickLoop) {
            this.tickLoop.start();
        }
        else if (this.worker) {
            this.worker.postMessage({ type: 'start' });
        }
    }
    /**
     * Pause the render/tick loop.
     *
     * The engine stops ticking but all state (buffers, params, descriptors)
     * is preserved. Call `start()` to resume. Safe to call multiple times.
     */
    pause() {
        if (this.singleThread && this.tickLoop) {
            this.tickLoop.stop();
        }
        else if (this.worker) {
            this.worker.postMessage({ type: 'pause' });
        }
    }
    /**
     * Set a factory function that can create fresh engine instances.
     * Used by the circuit breaker to recover from Wasm traps.
     * Without this, the circuit breaker can only retry the existing engine.
     */
    setEngineFactory(factory) {
        if (this.tickLoop) {
            this.tickLoop.setEngineFactory(factory);
        }
    }
    // --- Render (Single-Thread) ---
    renderSingleThread() {
        if (!this.ctx2d || !this.canvas)
            return;
        // If the Wasm adapter provides its own render(), it already drew to the canvas
        // during the engine tick. Skip the framework's default layer compositing —
        // it would overwrite the adapter's output with an empty descriptor table.
        if (this.wasmAdapter?.render)
            return;
        const layers = this.descriptorTable.getCompositeLayers();
        this.compositor.render(this.ctx2d, this.canvas.width, this.canvas.height, layers, (id) => this.buffers.get(id)?.sab ?? null);
        this.descriptorTable.clearAllDirty();
    }
    // --- Lifecycle ---
    /**
     * Get the compiled schema (byte offsets, CRC hash, param layout).
     * Useful for debugging or building tooling that inspects the ABI.
     */
    getSchema() {
        return this.schema;
    }
    /**
     * Get the descriptor table (buffer registry with generation counter).
     * Advanced use — most apps interact through `createBuffer`/`destroyBuffer`.
     */
    getDescriptorTable() {
        return this.descriptorTable;
    }
    /**
     * Get the computed memory layout (SAB offsets for params, ring, descriptors, error region).
     */
    getLayout() {
        return this.layout;
    }
    /**
     * Get the readback SharedArrayBuffer.
     * The overlay loop reads engine state (selection, transform, dirty, strokeOn)
     * from this buffer via Atomics.load / DataView reads.
     */
    getReadbackSAB() {
        return this.readbackSAB;
    }
    /**
     * Get a DataView over the readback SAB for convenient field access.
     */
    getReadbackView() {
        return this.readbackView;
    }
    /**
     * Drain commands from the ring buffer and forward to a handler.
     * Used by single-thread Wasm adapters that need to process commands
     * in their own format (the handler receives raw type + payload DataView).
     *
     * In worker mode, the worker drains commands internally — this method
     * is only used in single-thread mode.
     *
     * @param handler - Called for each command with (cmdType, payloadDataView)
     */
    drainCommandsForWasm(handler) {
        if (!this.ringConsumer)
            return;
        this.ringConsumer.drain((cmd) => { handler(cmd.type, cmd.payloadView); });
    }
    /**
     * Pre-flight check for runtime prerequisites. Returns a list of issues found.
     * An empty array means all checks passed.
     *
     * Call this before init() / initWorker() to get structured diagnostics
     * rather than catching mid-initialization errors.
     */
    validate(mode = 'single-thread') {
        const issues = [];
        if (mode === 'worker') {
            if (typeof SharedArrayBuffer === 'undefined') {
                issues.push('SharedArrayBuffer is not available. Worker mode requires COOP/COEP headers.');
            }
            if (typeof OffscreenCanvas === 'undefined') {
                issues.push('OffscreenCanvas is not available. Worker mode requires a browser that supports it.');
            }
        }
        if (this.schema.paramLayout.length === 0 && Object.keys(this.schema.commands).length === 0) {
            issues.push('Schema has no parameters and no commands — this is likely a misconfiguration.');
        }
        if (this.config.ringBufferCapacity < 4) {
            issues.push(`Ring buffer capacity ${this.config.ringBufferCapacity} is too small (minimum: 4).`);
        }
        if (this.config.paramBufferSize < 256) {
            issues.push(`Param buffer size ${this.config.paramBufferSize} is smaller than the error region (256 bytes).`);
        }
        return issues;
    }
    /**
     * Destroy the bridge and release all resources.
     *
     * - Stops the tick loop
     * - Terminates the Web Worker (if worker mode)
     * - Closes the sideband MessageChannel
     * - Releases all SharedArrayBuffers (via ref counting)
     * - Revokes the orchestrator blob URL
     * - Nulls all references for GC
     *
     * After calling destroy(), the bridge is inert — all method calls are no-ops.
     * Create a new HostBridge instance to reinitialize.
     */
    destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        this.pause();
        if (this.worker) {
            this.worker.postMessage({ type: 'destroy' });
            this.worker.terminate();
            this.worker = null;
        }
        if (this.sidebandChannel) {
            this.sidebandChannel.port1.close();
            this.sidebandChannel = null;
        }
        if (this.transformDataCache?.bitmap) {
            this.transformDataCache.bitmap.close();
        }
        this.buffers.clear();
        this.pendingDestroyAcks.clear();
        this.compositor.destroy();
        this.canvas = null;
        this.ctx2d = null;
        this.engineTick = null;
        this.tickLoop = null;
        this.wasmAdapter = null;
        this.readbackSAB = null;
        this.readbackView = null;
        this.selectionMaskCache = null;
        this.transformDataCache = null;
        this.exportResolve = null;
        this.adapterReadyResolve = null;
        this.adapterReadyPromise = null;
        this.onDimsChanged = null;
        if (this.orchestratorBlobUrl) {
            URL.revokeObjectURL(this.orchestratorBlobUrl);
            this.orchestratorBlobUrl = null;
        }
    }
}
//# sourceMappingURL=host-bridge.js.map